<template>
  <div class="p-session">
    <!-- Header -->
    <div class="p-session__head">
      <div class="p-session__title-block">
        <div class="p-session__eyebrow">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
          시험 세션
        </div>
        <div class="p-session__title">{{ exam?.examName || '시험 세션' }}</div>
        <div class="p-session__sub">
          <code class="p-code">ID: {{ exam?.examId || exam?._id || '-' }}</code>
        </div>
      </div>
      <div class="p-row-actions">
        <a-pill :kind="statusPillKind">{{ formatExamStatus(exam?.status) }}</a-pill>
        <a-btn kind="ghost" size="sm" @click="$emit('back')">
          <template #icon>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          </template>
          돌아가기
        </a-btn>
      </div>
    </div>

    <!-- KPI -->
    <div class="p-grid p-grid--kpi">
      <div class="p-kpi">
        <div class="p-kpi__head">
          <span class="p-kpi__label">시험 상태</span>
          <svg class="p-kpi__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        </div>
        <div class="p-kpi__value-row">
          <a-pill :kind="statusPillKind">{{ formatExamStatus(exam?.status) }}</a-pill>
        </div>
        <div class="p-kpi__foot p-kpi__foot--muted">현재 진행 상태</div>
      </div>

      <div class="p-kpi p-kpi--info">
        <div class="p-kpi__head">
          <span class="p-kpi__label">예정 시작</span>
          <svg class="p-kpi__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div class="p-kpi__value-row">
          <span class="p-kpi__value-text">{{ exam?.startTimeUTC ? formatDateTime(exam.startTimeUTC) : '-' }}</span>
        </div>
        <div class="p-kpi__foot p-kpi__foot--muted">시작 예정 시각</div>
      </div>

      <div class="p-kpi">
        <div class="p-kpi__head">
          <span class="p-kpi__label">예정 종료</span>
          <svg class="p-kpi__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div class="p-kpi__value-row">
          <span class="p-kpi__value-text">{{ exam?.endTimeUTC ? formatDateTime(exam.endTimeUTC) : '-' }}</span>
        </div>
        <div class="p-kpi__foot p-kpi__foot--muted">종료 예정 시각</div>
      </div>

      <div class="p-kpi p-kpi--warn">
        <div class="p-kpi__head">
          <span class="p-kpi__label">시험 시간</span>
          <svg class="p-kpi__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div class="p-kpi__value-row">
          <span class="p-kpi__value">{{ exam?.duration || '-' }}</span>
          <span v-if="exam?.duration" class="p-kpi__unit">분</span>
        </div>
        <div class="p-kpi__foot p-kpi__foot--muted">총 응시 시간</div>
      </div>
    </div>

    <!-- Exam Start Control -->
    <div
      v-if="exam && exam.settings && exam.settings.faceToFace === true"
      class="p-session__controls"
    >
      <div class="p-session__controls-text">
        <strong>대면 시험 시작</strong>
        <span>출석 확인 후 시험을 시작하세요. 시작 후에는 되돌릴 수 없습니다.</span>
      </div>
      <a-btn
        kind="primary"
        :disabled="isLoading || !canStart || currentStatus === 'active'"
        @click="onStartExam"
      >
        <template #icon>
          <span v-if="isLoading" class="p-spinner p-spinner--sm" />
          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        </template>
        {{ currentStatus === 'active' ? '시험 진행중' : '시험 시작' }}
      </a-btn>
    </div>

    <!-- Students List -->
    <a-card title="시험 응시 학생 목록" :sub="`총 ${students.length}명`">
      <template #action>
        <div class="p-row-actions">
          <a-pill kind="info">총 {{ students.length }}명</a-pill>
          <a-pill kind="ok">출석 {{ attendanceCount }}명</a-pill>
        </div>
      </template>

      <!-- Loading State -->
      <div v-if="isLoadingStudents" class="p-state">
        <div class="p-spinner" />
        <p>학생 목록을 불러오는 중...</p>
      </div>

      <!-- Empty State -->
      <a-empty v-else-if="students.length === 0" title="등록된 학생이 없습니다" />

      <!-- Students Table -->
      <a-table v-else :columns="studentCols" :rows="students" row-key="users_idx">
        <template #cell-no="{ index }">
          <span class="p-mono">{{ index + 1 }}</span>
        </template>
        <template #cell-name="{ row }">
          <div class="p-cell-class">
            <div class="p-user-avatar">{{ (row.userName || row.name || '?').slice(0, 2) }}</div>
            <div>
              <div class="p-cell-title">{{ row.userName || row.name || '-' }}</div>
              <div class="p-cell-sub">{{ row.userType || 'learner' }}</div>
            </div>
          </div>
        </template>
        <template #cell-email="{ row }">
          <span class="p-mono">{{ row.userEmail || row.email || '-' }}</span>
        </template>
        <template #cell-id="{ row }">
          <span class="p-mono">{{ row.users_idx || row.studentId || '-' }}</span>
        </template>
        <template #cell-attendance="{ row }">
          <a-pill :kind="row.attendance ? 'ok' : 'default'">
            {{ row.attendance ? '출석' : '미출석' }}
          </a-pill>
        </template>
        <template #cell-action="{ row }">
          <a-btn
            v-if="!row.attendance"
            kind="ghost"
            size="sm"
            :disabled="isMarkingAttendance[row.users_idx]"
            @click="markAttendance(row)"
          >
            <template #icon>
              <span v-if="isMarkingAttendance[row.users_idx]" class="p-spinner p-spinner--sm" />
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </template>
            출석 체크
          </a-btn>
          <a-pill v-else kind="ok">완료</a-pill>
        </template>
      </a-table>

      <!-- Batch Actions -->
      <div v-if="students.length > 0" class="p-session__batch">
        <div class="p-row-actions">
          <a-btn kind="ghost" size="sm" @click="markAllAttendance">
            <template #icon>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </template>
            전체 출석 체크
          </a-btn>
          <a-btn kind="ghost" size="sm" @click="clearAllAttendance">
            <template #icon>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </template>
            출석 초기화
          </a-btn>
        </div>
        <div class="p-session__rate">
          출석률 <strong class="p-mono">{{ attendanceRate }}%</strong>
        </div>
      </div>
    </a-card>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch, reactive, getCurrentInstance } from 'vue'
import { useExam } from '@/composables/useExam'
import { formatKoreanDateTime, formatUtcToTimeZone } from '@/utils/datetime'
import ACard from '@/components/Pineapple/ACard.vue'
import ATable from '@/components/Pineapple/ATable.vue'
import APill from '@/components/Pineapple/APill.vue'
import ABtn from '@/components/Pineapple/ABtn.vue'
import AEmpty from '@/components/Pineapple/AEmpty.vue'

export default {
  name: 'ExamSession',
  components: { ACard, ATable, APill, ABtn, AEmpty },
  props: {
    exam: {
      type: Object,
      default: null
    },
    classroomIdx: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['back', 'exam-updated'],
  setup(props, { emit }) {
    const {
      fetchExam,
      fetchExamClassroom,
      fetchExamStudents,
      fetchAttendance,
      saveAttendance,
      clearAttendance,
      startExam,
      formatExamStatus,
      isLoading
    } = useExam()

    // Get instance for accessing global properties like $notifyVue
    const instance = getCurrentInstance()
    const notifyVue = (options) => {
      // Access $notifyVue through proxy (added by common.js plugin)
      try {
        if (instance?.proxy && instance.proxy['$notifyVue']) {
          instance.proxy['$notifyVue'](options)
        } else {
          console.log('Notification:', options)
        }
      } catch (error) {
        console.log('Notification:', options)
      }
    }
    const students = ref([])
    const isLoadingStudents = ref(false)
    const selectedClassroom = ref(props.classroomIdx || '')
    const isMarkingAttendance = reactive({})
    const examClassroom = ref(null)
    const classrooms = ref([])

    const studentCols = [
      { key: 'no', label: '#', width: '60px' },
      { key: 'name', label: '학생 이름' },
      { key: 'email', label: '이메일' },
      { key: 'id', label: 'ID', width: '120px' },
      { key: 'attendance', label: '출석 상태', width: '120px', align: 'center' },
      { key: 'action', label: '출석 체크', width: '150px', align: 'center' },
    ]

    const canStart = computed(() => {
      return !!props.exam?.examId && !!selectedClassroom.value
    })

    const attendanceCount = computed(() => {
      return students.value.filter(s => s.attendance).length
    })

    const attendanceRate = computed(() => {
      if (students.value.length === 0) return 0
      return Math.round((attendanceCount.value / students.value.length) * 100)
    })

    const currentStatus = ref(props.exam?.status || 'scheduled')

    // 시험 상태 → Pineapple APill kind
    const statusPillKind = computed(() => {
      const map = {
        draft: 'default',
        scheduled: 'warn',
        active: 'ok',
        completed: 'info'
      }
      return map[currentStatus.value] || 'default'
    })

    // UTC 시간을 타임존에 맞게 변환하여 표시
    const formatDateTime = (utcTime) => {
      if (!utcTime) return ''
      const localTime = formatUtcToTimeZone(utcTime, props.exam?.timeZone || 'Asia/Seoul')
      return formatKoreanDateTime(localTime)
    }

    const refreshStudents = async () => {
      if (!selectedClassroom.value || !props.exam?.examId) {
        students.value = []
        return
      }

      isLoadingStudents.value = true
      try {
        // 학생 목록과 출석 정보를 병렬로 가져오기
        const [studentsList, attendanceList] = await Promise.all([
          fetchExamStudents(selectedClassroom.value, props.exam.examId),
          fetchAttendance(props.exam.examId)
        ])

        // 출석 정보를 users_idx를 키로 하는 Map으로 변환
        const attendanceMap = new Map()
        if (Array.isArray(attendanceList)) {
          attendanceList.forEach(attendance => {
            if (attendance.users_idx !== undefined) {
              attendanceMap.set(attendance.users_idx, true)
            }
          })
        }

        // 학생 목록에 출석 정보 매핑
        students.value = (studentsList || []).map(student => ({
          ...student,
          attendance: attendanceMap.has(student.users_idx) || false
        }))
      } catch (error) {
        console.error('Error fetching students:', error)
        notifyVue({ type: 'error', title: '학생 목록을 불러오는데 실패했습니다.' })
        students.value = []
      } finally {
        isLoadingStudents.value = false
      }
    }

    const onStartExam = async () => {
      if (!props.exam?.examId) {
        notifyVue({ type: 'warning', title: '시험 ID가 없습니다.' })
        return
      }

      if (!selectedClassroom.value) {
        notifyVue({ type: 'warning', title: '강의실을 선택해주세요.' })
        return
      }

      // 타임존을 고려하여 UTC 시간을 해당 타임존의 로컬 시간으로 변환
      const timeZone = props.exam.timeZone || 'Asia/Seoul'
      //console.log('props.exam.startTimeUTC:', props.exam.startTimeUTC);
      //console.log('props.exam.timeZone:', timeZone);

      // UTC 시간을 타임존 로컬 시간 문자열로 변환
      const startTimeLocalStr = formatUtcToTimeZone(props.exam.startTimeUTC, timeZone)
      const nowLocalStr       = formatUtcToTimeZone(new Date(), timeZone)

      // 로컬 시간 문자열을 Date 객체로 변환하여 비교
      // (타임존 정보 없이 파싱되므로 브라우저의 로컬 타임존으로 해석됨)
      const startTime = new Date(startTimeLocalStr)
      const now = new Date(nowLocalStr)

      if(startTime > now) {
        console.log(`startTime (${timeZone}):`, startTimeLocalStr);
        console.log(`now (${timeZone}):`, nowLocalStr);
        console.log('startTime (Date):', startTime);
        console.log('now (Date):', now);
        notifyVue({ type: 'warning', title: '시험 시작 시간이 아닙니다.' });
        return;
      }

      try {
        const confirmed = window.confirm('정말로 시험을 시작하시겠습니까?\n시작 후에는 되돌릴 수 없습니다.')
        if (!confirmed) return

        await startExam(props.exam.examId, parseInt(selectedClassroom.value))
        currentStatus.value = 'active'
        notifyVue({ type: 'success', title: '시험이 시작되었습니다.' })

        // 시험 정보 다시 가져와 부모에 전달 (시작/종료 시간 등 갱신 반영)
        // 재조회가 실패/널이어도 반드시 모니터 화면으로 전환되도록 로컬 정보로 폴백 emit 한다.
        let updatedExam = null
        try {
          updatedExam = await fetchExam(props.exam.examId)
        } catch (e) {
          console.error('Failed to re-fetch exam after start:', e)
        }
        emit('exam-updated', updatedExam || { ...props.exam, status: 'active' })

        // 학생 목록 새로고침
        await refreshStudents()
      } catch (error) {
        console.error('Error starting exam:', error)
        notifyVue({ type: 'error', title: '시험 시작에 실패했습니다.' })
      }
    }

    const markAttendance = async (student) => {
      if (!props.exam?.examId || !selectedClassroom.value) {
        notifyVue({ type: 'warning', title: '시험 정보가 올바르지 않습니다.' })
        return
      }

      isMarkingAttendance[student.users_idx] = true

      try {
        const attendanceData = {
          exam_id: props.exam.examId,
          classroom_idx: parseInt(selectedClassroom.value),
          users_idx: student.users_idx,
          user_email: student.userEmail || student.email
        }

        await saveAttendance(attendanceData)

        // 로컬 상태 업데이트
        const index = students.value.findIndex(s => s.users_idx === student.users_idx)
        if (index !== -1) {
          students.value[index].attendance = true
        }

        //notifyVue({ type: 'success', title: `${student.userName || student.name}님의 출석이 체크되었습니다.` })
      } catch (error) {
        console.error('Error marking attendance:', error)
        notifyVue({ type: 'error', title: '출석 체크에 실패했습니다.' })
      } finally {
        isMarkingAttendance[student.users_idx] = false
      }
    }

    const markAllAttendance = async () => {
      if (!props.exam?.examId || !selectedClassroom.value) {
        notifyVue({ type: 'warning', title: '시험 정보가 올바르지 않습니다.' })
        return
      }

      const unattendedStudents = students.value.filter(s => !s.attendance)

      if (unattendedStudents.length === 0) {
        notifyVue({ type: 'info', title: '이미 모든 학생이 출석 처리되었습니다.' })
        return
      }

      for (const student of unattendedStudents) {
        await markAttendance(student)
      }

      notifyVue({ type: 'success', title: '전체 출석 체크가 완료되었습니다.' })
    }

    const clearAllAttendance = async () => {
      if (!props.exam?.examId || !selectedClassroom.value) {
        notifyVue({ type: 'warning', title: '시험 정보가 올바르지 않습니다.' })
        return
      }

      try {
        await clearAttendance(props.exam.examId, parseInt(selectedClassroom.value))

        // 로컬 상태 업데이트
        students.value = students.value.map(student => ({
          ...student,
          attendance: false
        }))

        notifyVue({ type: 'success', title: '출석 정보가 데이터베이스에서 초기화되었습니다.' })
      } catch (error) {
        console.error('Error clearing attendance:', error)
        notifyVue({ type: 'error', title: '출석 초기화에 실패했습니다.' })
      }
    }

    // 시험에 연결된 클래스 정보 가져오기
    const loadExamClassroom = async () => {
      if (!props.exam?.examId) return

      try {
        const classroom = await fetchExamClassroom(props.exam.examId)
        if (classroom) {
          examClassroom.value = classroom

          // 클래스 정보가 있으면 선택 옵션에 추가하고 자동 선택
          classrooms.value = [{
            classroomIdx: classroom.idx,
            className: classroom.name
          }]

          // 자동으로 클래스 선택
          if (!selectedClassroom.value) {
            selectedClassroom.value = classroom.idx
          }
        }
      } catch (error) {
        console.error('Error loading exam classroom:', error);
      }
    }

    onMounted(async () => {
      if (props.exam?.status) currentStatus.value = props.exam.status
      // 먼저 클래스 정보를 가져온다
      await loadExamClassroom()

      // 그 다음 학생 목록을 가져온다
      if (props.exam?.examId && selectedClassroom.value) {
        refreshStudents()
      }
    })

    // Watch for classroom change
    watch(selectedClassroom, (newVal) => {
      if (newVal && props.exam?.examId) {
        refreshStudents()
      }
    })

    // Watch for exam prop changes
    watch(() => props.exam?.status, (newStatus) => {
      if (newStatus) currentStatus.value = newStatus
    })

    watch(() => props.exam?.examId, (newExamId) => {
      if (newExamId && selectedClassroom.value) {
        refreshStudents()
      }
    })

    return {
      students,
      studentCols,
      isLoadingStudents,
      selectedClassroom,
      classrooms,
      isMarkingAttendance,
      isLoading,
      canStart,
      attendanceCount,
      attendanceRate,
      onStartExam,
      refreshStudents,
      markAttendance,
      markAllAttendance,
      clearAllAttendance,
      formatDateTime,
      formatExamStatus,
      statusPillKind,
      currentStatus
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-session {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
}

/* Header */
.p-session__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: 12px;
}
.p-session__title-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.p-session__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 800;
  color: $p-point-deep;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.p-session__title {
  font-size: 18px;
  font-weight: 800;
  color: $p-ink;
}
.p-session__sub {
  font-size: 12px;
  color: $p-ink-mid;
}

.p-row-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* KPI */
.p-grid {
  display: grid;
  gap: 12px;
}
.p-grid--kpi {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  @media (max-width: 768px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.p-kpi {
  position: relative;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 124px;
  overflow: hidden;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: $p-line-hard;
  }
  &:hover { border-color: $p-line-hard; transform: translateY(-1px); }

  &--warn::before { background: $p-warn; }
  &--ok::before { background: $p-ok; }
  &--info::before { background: $p-point-deep; }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  &__label {
    font-size: 11px;
    font-weight: 800;
    color: $p-ink-mid;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  &__icon {
    width: 16px;
    height: 16px;
    color: $p-ink-soft;
    flex-shrink: 0;
  }
  &__value-row {
    display: flex;
    align-items: baseline;
    gap: 4px;
    flex: 1;
  }
  &__value {
    font-family: $p-font-mono;
    font-size: 28px;
    font-weight: 800;
    color: $p-ink;
    line-height: 1.1;
  }
  &__value-text {
    font-size: 14px;
    font-weight: 700;
    color: $p-ink;
    line-height: 1.35;
  }
  &__unit {
    font-size: 12px;
    color: $p-ink-mid;
    font-weight: 600;
  }
  &__foot {
    font-size: 11px;
    color: $p-ink;
    min-height: 14px;
    display: flex;
    align-items: center;

    &--muted { color: $p-ink-soft; }
  }
}

/* Exam start control */
.p-session__controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  border-radius: 12px;

  &-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    strong {
      font-size: 13px;
      font-weight: 800;
      color: $p-ink;
    }
    span {
      font-size: 12px;
      color: $p-ink-mid;
    }
  }
}

/* Batch actions */
.p-session__batch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid $p-line-soft;
}
.p-session__rate {
  font-size: 12px;
  color: $p-ink-mid;

  strong {
    font-size: 14px;
    color: $p-ink;
    margin-left: 4px;
  }
}

/* Shared cell bits */
.p-code {
  font-family: $p-font-mono;
  font-size: 11px;
  padding: 2px 6px;
  background: $p-panel-alt;
  border: 1px solid $p-line-soft;
  border-radius: 4px;
  color: $p-ink-mid;
}

.p-mono { font-family: $p-font-mono; font-size: 12px; color: $p-ink2; }

.p-cell-class {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.p-cell-title { font-size: 13px; font-weight: 700; color: $p-ink; }
.p-cell-sub { font-size: 11px; color: $p-ink-mid; }

.p-user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: $p-point-soft;
  color: $p-ink;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  flex-shrink: 0;
}

/* States */
.p-state {
  padding: 40px 0;
  text-align: center;
  color: $p-ink-mid;
  font-size: 13px;
  p { margin: 12px 0 0; }
}
.p-spinner {
  display: inline-block;
  width: 36px; height: 36px;
  border: 3px solid $p-line; border-top-color: $p-point;
  border-radius: 50%;
  animation: p-spin 0.8s linear infinite;

  &--sm {
    width: 13px; height: 13px;
    border-width: 2px;
  }
}
@keyframes p-spin { to { transform: rotate(360deg); } }
</style>
